import { Injectable, inject, OnDestroy } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { interval, Subscription } from 'rxjs';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class TokenRefreshService implements OnDestroy {
  private keycloak = inject(KeycloakService);
  private router = inject(Router);
  private refreshSubscription?: Subscription;
  private readonly CHECK_INTERVAL = 60000; // Check every minute
  private readonly REFRESH_THRESHOLD = 120; // Refresh if expires in < 2 minutes

  /**
   * Start automatic token refresh checks
   * Called after successful login
   */
  startAutoRefresh(): void {
    // Stop any existing refresh
    this.stopAutoRefresh();

    // Start periodic check
    this.refreshSubscription = interval(this.CHECK_INTERVAL).subscribe(() => {
      this.checkAndRefreshToken();
    });

    console.log('[TokenRefresh] Auto-refresh started');
  }

  /**
   * Stop automatic token refresh
   * Called on logout or service destruction
   */
  stopAutoRefresh(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
      this.refreshSubscription = undefined;
      console.log('[TokenRefresh] Auto-refresh stopped');
    }
  }

  /**
   * Check token expiration and refresh if needed
   */
  private async checkAndRefreshToken(): Promise<void> {
    try {
      const keycloakInstance = this.keycloak.getKeycloakInstance();

      // Check if user is logged in
      if (!keycloakInstance.authenticated) {
        this.stopAutoRefresh();
        return;
      }

      // Get token expiration time
      const tokenParsed = keycloakInstance.tokenParsed;
      if (!tokenParsed?.exp) {
        console.warn('[TokenRefresh] Token expiration not available');
        return;
      }

      const now = Math.floor(Date.now() / 1000);
      const expiresIn = tokenParsed.exp - now;

      console.log(`[TokenRefresh] Token expires in ${expiresIn} seconds`);

      // Refresh if token expires soon
      if (expiresIn < this.REFRESH_THRESHOLD) {
        console.log('[TokenRefresh] Refreshing token proactively...');

        const refreshed = await keycloakInstance.updateToken(this.REFRESH_THRESHOLD);

        if (refreshed) {
          console.log('[TokenRefresh] Token refreshed successfully');
        } else {
          console.log('[TokenRefresh] Token still valid, no refresh needed');
        }
      }
    } catch (error: any) {
      console.error('[TokenRefresh] Failed to refresh token:', error);

      // If refresh token is expired, logout user
      if (error?.error === 'invalid_grant' || error?.error_description?.includes('Token is not active')) {
        console.error('[TokenRefresh] Refresh token expired, logging out...');
        await this.handleExpiredRefreshToken();
      }
    }
  }

  /**
   * Handle expired refresh token by logging out user
   * Better UX than redirecting to login page unexpectedly
   */
  private async handleExpiredRefreshToken(): Promise<void> {
    try {
      this.stopAutoRefresh();

      // Clear any local state
      const keycloakInstance = this.keycloak.getKeycloakInstance();

      // Logout from Keycloak
      await keycloakInstance.logout({
        redirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('[TokenRefresh] Error during logout:', error);
      // Force reload as fallback
      window.location.href = '/';
    }
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }
}
